import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Box, Button, Card, Group, Stack, Title, Tooltip } from "@mantine/core";

import { IconRefresh } from "@tabler/icons-react";

import Tabs, { ScrollingTabsList } from "@/components/Tabs";
import { DevelopmentFeatureFlags, PracticeFeatureFlags, useFeature } from "@/feature-flags";
import NotFound from "@/NotFound";
import PermissionGate from "@/permission/components/PermissionGate";
import {
  ALL_PERMISSION_FLAGS,
  BILLER_MANAGER_PERMISSION_FLAGS,
} from "@/permission/constants/practitioner-permissions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { parseEnumParam, TAB_PARAM, updateSearchParams } from "@/utils/search-params";

import { REFRESH_ELIGIBILITY_TABLE_LABEL } from "./constants/eligibility";
import EligibilityTable from "./EligibilityTable";
import Ledger from "./Ledger";
import { REFRESH_PRACTICE_LEDGER_TABLE_LABEL } from "./Ledger/constants/practice-ledger";
import MissingServices from "./MissingServices";
import { REFRESH_MISSING_SERVICES_TABLE_LABEL } from "./MissingServices/constants/missing-services";
import OpenStatements from "./OpenStatements";
import { REFRESH_OPEN_STATEMENTS_TABLE_LABEL } from "./OpenStatements/constants/open-statements";
import {
  BILLER_MANAGER_ELIGIBILITY_TABS,
  ELIGIBILITY_TABS,
  EligibilityTab,
} from "./types/eligibility";

export default function PracticeBilling() {
  const isPracticeBillingDevelopmentEnabled = useFeature(DevelopmentFeatureFlags.PracticeBilling);
  const isBillingFeatureEnabled = useFeature(PracticeFeatureFlags.Billing);
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasBillerManagerPermission } = usePractitionerPermissions();
  const requestedTab = parseEnumParam(
    ELIGIBILITY_TABS,
    searchParams.get(TAB_PARAM),
    "eligibility-status",
  );
  const isBillerManagerTabDisabled = !hasBillerManagerPermission;
  const activeTab = resolveActiveTab(requestedTab, isBillerManagerTabDisabled);
  const [refreshKeyByTab, setRefreshKeyByTab] = useState<RefreshKeyByTab>({
    "eligibility-status": 0,
    "missing-services": 0,
    "open-statements": 0,
    ledger: 0,
  });
  const [loadingByTab, setLoadingByTab] = useState<LoadingByTab>({
    "eligibility-status": false,
    "missing-services": false,
    "open-statements": false,
    ledger: false,
  });

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          updateSearchParams(next, { [TAB_PARAM]: value });
          return next;
        },
        { preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  const handleRefresh = useCallback(() => {
    setRefreshKeyByTab((previous) => ({
      ...previous,
      [activeTab]: previous[activeTab] + 1,
    }));
  }, [activeTab, setRefreshKeyByTab]);

  const handleEligibilityLoadingChange = useCallback(
    (isLoading: boolean) => {
      setLoadingByTab((previous) => ({ ...previous, "eligibility-status": isLoading }));
    },
    [setLoadingByTab],
  );
  const handleMissingServicesLoadingChange = useCallback(
    (isLoading: boolean) => {
      setLoadingByTab((previous) => ({ ...previous, "missing-services": isLoading }));
    },
    [setLoadingByTab],
  );
  const handleOpenStatementsLoadingChange = useCallback(
    (isLoading: boolean) => {
      setLoadingByTab((previous) => ({ ...previous, "open-statements": isLoading }));
    },
    [setLoadingByTab],
  );
  const handleLedgerLoadingChange = useCallback(
    (isLoading: boolean) => {
      setLoadingByTab((previous) => ({ ...previous, ledger: isLoading }));
    },
    [setLoadingByTab],
  );

  const isActiveTabLoading = loadingByTab[activeTab];
  const refreshLabel = REFRESH_TABLE_LABEL_BY_TAB[activeTab];

  if (!isPracticeBillingDevelopmentEnabled || !isBillingFeatureEnabled) {
    return <NotFound />;
  }

  return (
    <PermissionGate
      allowPatient={false}
      fallback={<NotFound />}
      requiredPermissions={ALL_PERMISSION_FLAGS}
    >
      <Card px="xl" py="lg">
        <Stack gap="lg">
          <Group wrap="nowrap" align="center" justify="space-between">
            <Title order={2}>Billing</Title>
            <Tooltip label={refreshLabel}>
              <Box component="span" style={{ display: "inline-block" }}>
                <Button
                  px="md"
                  variant="subtle"
                  onClick={handleRefresh}
                  aria-label={refreshLabel}
                  loading={isActiveTabLoading}
                  disabled={isActiveTabLoading}
                >
                  <IconRefresh size={18} />
                </Button>
              </Box>
            </Tooltip>
          </Group>
          {/* Panels unmount when inactive so the Missing Services searches only run once that tab
              is opened. Eligibility Status opts back in below to keep its existing behavior. */}
          <Tabs value={activeTab} keepMounted={false} onChange={handleTabChange}>
            <ScrollingTabsList>
              <Tabs.Tab value="eligibility-status" data-testid="eligibility-status-tab">
                Eligibility Status
              </Tabs.Tab>
              <Tabs.Tab
                value="missing-services"
                data-testid="missing-services-tab"
                disabled={isBillerManagerTabDisabled}
              >
                Missing Services
              </Tabs.Tab>
              <Tabs.Tab value="open-statements" data-testid="open-statements-tab">
                Open Statements
              </Tabs.Tab>
              <Tabs.Tab
                value="ledger"
                data-testid="ledger-tab"
                disabled={isBillerManagerTabDisabled}
              >
                Ledger
              </Tabs.Tab>
            </ScrollingTabsList>
            <Tabs.Panel pt="lg" keepMounted value="eligibility-status">
              <EligibilityTable
                onLoadingChange={handleEligibilityLoadingChange}
                refreshKey={refreshKeyByTab["eligibility-status"]}
              />
            </Tabs.Panel>
            <Tabs.Panel pt="lg" value="missing-services">
              <PermissionGate
                allowPatient={false}
                requiredPermissions={BILLER_MANAGER_PERMISSION_FLAGS}
              >
                <MissingServices
                  refreshKey={refreshKeyByTab["missing-services"]}
                  onLoadingChange={handleMissingServicesLoadingChange}
                />
              </PermissionGate>
            </Tabs.Panel>
            <Tabs.Panel pt="lg" value="open-statements">
              <OpenStatements
                refreshKey={refreshKeyByTab["open-statements"]}
                onLoadingChange={handleOpenStatementsLoadingChange}
              />
            </Tabs.Panel>
            <Tabs.Panel pt="lg" value="ledger">
              <PermissionGate
                allowPatient={false}
                requiredPermissions={BILLER_MANAGER_PERMISSION_FLAGS}
              >
                <Ledger
                  refreshKey={refreshKeyByTab["ledger"]}
                  onLoadingChange={handleLedgerLoadingChange}
                />
              </PermissionGate>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Card>
    </PermissionGate>
  );
}

/**
 * Missing Services and Ledger are Biller/Manager only. Providers, Doulas and Scribes reach this
 * page for Eligibility Status, so fall back rather than honoring a restricted tab from the URL.
 */
function resolveActiveTab(
  requestedTab: EligibilityTab,
  isBillerManagerTabDisabled: boolean,
): EligibilityTab {
  if (isBillerManagerTabDisabled && isBillerManagerEligibilityTab(requestedTab)) {
    return "eligibility-status";
  }
  return requestedTab;
}

function isBillerManagerEligibilityTab(tab: EligibilityTab): boolean {
  return BILLER_MANAGER_ELIGIBILITY_TABS.some((restrictedTab) => restrictedTab === tab);
}

/** Tooltip + aria-label for the header refresh button, per active tab. */
const REFRESH_TABLE_LABEL_BY_TAB: Record<EligibilityTab, string> = {
  "eligibility-status": REFRESH_ELIGIBILITY_TABLE_LABEL,
  "missing-services": REFRESH_MISSING_SERVICES_TABLE_LABEL,
  "open-statements": REFRESH_OPEN_STATEMENTS_TABLE_LABEL,
  ledger: REFRESH_PRACTICE_LEDGER_TABLE_LABEL,
};

type RefreshKeyByTab = Record<EligibilityTab, number>;
type LoadingByTab = Record<EligibilityTab, boolean>;
